import { useEffect, useState } from "react";
import { Percent, Trash2 } from "lucide-react";
import { getMyInfo, updateMyInfo } from "interfaces/user";
import { toast } from "react-toastify";
import SaveStatus from "components/actions/SaveStatus";

interface VatAccountingCodeRow {
  vat: number | string;
  code: string;
}

const VatAccountingCodeList: React.FC = () => {
  const [codes, setCodes] = useState<VatAccountingCodeRow[]>([]);
  const [isParent, setIsParent] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [firstUpdate, setFirstUpdate] = useState(false);
  const [saveStatus, setSaveStatus] = useState<any>("none");

  const initUser = async () => {
    try {
      const res = await getMyInfo();
      if (res?.data) {
        const fromDb = Array.isArray(res.data.vatAccountingCodes) ? res.data.vatAccountingCodes : [];
        setCodes(fromDb);
        setIsParent(!res.data.ownerId);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setInitialized(true);
    }
  };

  useEffect(() => {
    initUser();
  }, []);

  useEffect(() => {
    if (!initialized || !firstUpdate) return;

    setSaveStatus("saving");
    const delayDebounceFn = setTimeout(async () => {
      try {
        const vatAccountingCodes = codes
          .filter((row) => row.vat !== "" && row.vat !== null && row.code.trim() !== "")
          .map((row) => ({ vat: Number(row.vat), code: row.code.trim() }));

        const { data, error } = await updateMyInfo({ vatAccountingCodes });
        if (error || !data) {
          setSaveStatus("error");
          if (error?.error) toast.error(error.error);
          return;
        }
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("none"), 1500);
      } catch (err) {
        console.error(err);
        setSaveStatus("error");
      }
    }, 600);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, codes]);

  const guardEdit = () => {
    if (!isParent) {
      toast.error("Seul le compte principal peut modifier cette configuration.");
      return false;
    }
    return true;
  };

  const handleAdd = () => {
    if (!guardEdit()) return;
    setFirstUpdate(true);
    setCodes([...codes, { vat: "", code: "" }]);
  };

  const handleChange = (index: number, field: "vat" | "code", value: string) => {
    if (!guardEdit()) return;
    setFirstUpdate(true);
    setCodes(codes.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleRemove = (index: number) => {
    if (!guardEdit()) return;
    setFirstUpdate(true);
    setCodes(codes.filter((_, i) => i !== index));
  };

  return (
    <div className="">
      <div className="bg-white border rounded-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-gray-900 font-semibold text-2xl mb-2">Codes comptables par taux de TVA</h1>
              <p className="text-gray-600 text-sm">
                Associez un code comptable à chaque taux de TVA. Il remplacera le compte de TVA par
                défaut sur les exports comptables dès qu'une facture utilise ce taux.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <SaveStatus status={saveStatus} />
              <button
                className="bg-toola-500 text-white rounded-lg px-4 py-2 hover:bg-opacity-90 transition-colors flex items-center gap-2 disabled:opacity-50"
                onClick={handleAdd}
                disabled={!isParent}
              >
                <span className="text-lg">+</span>
                Ajouter un taux
              </button>
            </div>
          </div>
          {!isParent && (
            <p className="text-gray-500 text-xs mt-3">
              Seul le compte principal peut modifier cette configuration. Elle s'applique à toute l'entreprise.
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {codes.length === 0 ? (
          <div className="bg-white border-gray-100 rounded-lg shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Percent className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-gray-900 font-semibold text-lg mb-2">Aucun code comptable configuré</h3>
            <p className="text-gray-600 text-sm mb-6">
              Par défaut, les exports comptables utilisent le compte de TVA standard du modèle choisi.
            </p>
            <button
              className="bg-toola-500 text-white rounded-lg px-4 py-2 hover:bg-opacity-90 transition-colors disabled:opacity-50"
              onClick={handleAdd}
              disabled={!isParent}
            >
              Ajouter mon premier taux
            </button>
          </div>
        ) : (
          <div className="bg-white border-gray-100 rounded-lg shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-3">
                  <span className="text-gray-600 text-sm font-medium">Taux de TVA (%)</span>
                </div>
                <div className="col-span-7">
                  <span className="text-gray-600 text-sm font-medium">Code comptable</span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="text-gray-600 text-sm font-medium">Actions</span>
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {codes.map((row, index) => (
                <div
                  key={index}
                  className={`px-6 py-4 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        max={100}
                        placeholder="20"
                        value={row.vat}
                        disabled={!isParent}
                        onChange={(e) => handleChange(index, "vat", e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-toola-500 disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                    <div className="col-span-7">
                      <input
                        type="text"
                        placeholder="4457110000"
                        value={row.code}
                        disabled={!isParent}
                        onChange={(e) => handleChange(index, "code", e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-toola-500 disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <button
                        className="bg-red-400 text-white rounded-lg px-3 py-1.5 text-sm hover:bg-red-500 transition-colors disabled:opacity-50"
                        disabled={!isParent}
                        onClick={() => handleRemove(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">
                  {codes.length} taux configuré{codes.length > 1 ? "s" : ""}
                </span>
                <button
                  className="text-toola-500 text-sm hover:underline transition-colors disabled:opacity-50 disabled:no-underline"
                  onClick={handleAdd}
                  disabled={!isParent}
                >
                  + Ajouter un autre taux
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VatAccountingCodeList;
